import React, { useEffect, useState } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { CSVLink } from "react-csv";
import { baseurl } from "../../baseurl";
import { FiEye, FiTrash2, FiEdit2, FiRotateCcw } from "react-icons/fi";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

function AllInvoice() {
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(""); 
  const [endDate, setEndDate] = useState(""); 
  const navigate = useNavigate();

  // Fetch invoices
  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseurl}invoices`);
      if (res.data.success) {
        setInvoices(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();

    // Initialize jQuery datepickers
    if (window.$) {
      window.$("#startDate").datepicker({
        dateFormat: "yy-mm-dd",
        onSelect: function (dateText) {
          setStartDate(dateText);
        },
      });
      window.$("#endDate").datepicker({
        dateFormat: "yy-mm-dd",
        onSelect: function (dateText) {
          setEndDate(dateText);
        },
      });
    }
  }, []);

  // Filter invoices by search + date
  const filtered = invoices.filter((i) => {
    const matchesSearch =
      String(i.id).includes(search) ||
      i.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      i.phone?.includes(search) ||
      i.payment_mode?.toLowerCase().includes(search.toLowerCase());

    const invoiceDate = new Date(i.date);
    const isAfterStart = startDate ? invoiceDate >= new Date(startDate) : true;
    const isBeforeEnd = endDate ? invoiceDate <= new Date(endDate) : true;

    return matchesSearch && isAfterStart && isBeforeEnd;
  });

  const handleView = (id) => navigate(`/invoice/${id}`);
  const handleEdit = (id) => navigate(`/invoice/edit/${id}`);
  const handleReturn = (id) => navigate(`/invoice/${id}/return`);

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Invoice?",
      text: "This will permanently remove the invoice record.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (confirm.isConfirmed) {
      try {
        await axios.delete(`${baseurl}invoices/${id}`);
        Swal.fire("Deleted!", "Invoice removed successfully.", "success");
        fetchInvoices();
      } catch {
        Swal.fire("Error", "Failed to delete invoice.", "error");
      }
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="main-content">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="breadcrumb-title mb-0">🧾 All Invoices</h4>
            <div className="d-flex gap-2 align-items-center">
              {/* Search */}
              <input
                type="text"
                className="form-control"
                style={{ width: 250 }}
                placeholder="Search by name, phone, payment mode..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {/* Start Date */}
              <input
                type="text"
                id="startDate"
                className="form-control"
                placeholder="Start Date"
                autoComplete="off"
              />

              {/* End Date */}
              <input
                type="text"
                id="endDate"
                className="form-control"
                placeholder="End Date"
                 autoComplete="off"
              />

              <CSVLink
                data={filtered}
                filename={"invoices.csv"}
                className="btn btn-primary"
              >
                Export CSV
              </CSVLink>
            </div>
          </div>

          {/* Table */}
          <div className="card shadow-sm border-0">
            <div className="card-body table-responsive">
              {loading ? (
                <table className="table text-center">
                  <thead>
                    <tr>
                      {[...Array(11)].map((_, idx) => (
                        <th key={idx}>
                          <Skeleton width={80} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(6)].map((_, i) => (
                      <tr key={i}>
                        {Array(11)
                          .fill()
                          .map((_, j) => (
                            <td key={j}>
                              <Skeleton height={20} />
                            </td>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : filtered.length > 0 ? (
                <table className="table table-striped align-middle text-center">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Customer Name</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th>Date</th>
                      <th>Payment Mode</th>
                      <th>Total ₹</th>  
                      <th>Items</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((inv) => (
                      <tr key={inv.id}>
                        <td>{inv.id}</td>
                        <td className="fw-semibold">{inv.full_name}</td>
                        <td>{inv.phone}</td>
                        <td className="text-truncate" style={{ maxWidth: 150 }}>
                          {inv.address || "-"}
                        </td>
                        <td>{new Date(inv.date).toLocaleDateString("en-GB")}</td>
                        <td>
                          <span className="badge bg-info text-dark text-uppercase">
                            {inv.payment_mode}
                          </span>
                        </td>
                        <td>₹{inv.total_amount}</td>  
                        <td>
                          <span className="badge bg-secondary">{inv.items_count}</span>
                        </td>
                        <td>
                          <div className="d-flex justify-content-center gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              title="View Invoice"
                              onClick={() => handleView(inv.id)}
                            >
                              <FiEye size={16} />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-success"
                              title="Edit Invoice"
                              onClick={() => handleEdit(inv.id)}
                            >
                              <FiEdit2 size={16} />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-warning"
                              title="Return Items"
                              onClick={() => handleReturn(inv.id)}
                            >
                              <FiRotateCcw size={16} />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Delete Invoice"
                              onClick={() => handleDelete(inv.id)}
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center py-3">No invoices found.</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default AllInvoice;
